import { IconButton, RefreshIcon } from "@minervaproject/fds";
import { useRef } from "react";

import { OpenInNewTab } from "./controls/open_in_new_tab";
import { ResourceTitle } from "./controls/resource_title";
import { ExternalResourceWarning } from "./external_resource_warning";

import { CenteredErrorBoundary, TooltipErrorBoundary } from "components/error_boundary";
import { useAppSelector } from "hooks/redux_hooks";
import { LocalModalContentType, ResourceType, ResourceTypeType } from "shared/types";
import { localSelectors } from "store";
import { getGoogleDriveUrl } from "utils/resource";

function ResourceLoadingError() {
  return (
    <div
      className="flex flex-column items-center justify-center h-full bg-white p2"
      data-testid="resource-loading-error"
    >
      <p className="mb1">Resource is still being created...</p>
      <p className="text-gray mb2">
        This usually takes a few seconds. If it persists, contact support.
      </p>
    </div>
  );
}

function IframeResource({ resource }: { resource: ResourceType }) {
  const localModalContent = useAppSelector(localSelectors.selectLocalModalContent());
  const iframeRef = useRef<HTMLIFrameElement>(null);
  let resourceUrl = resource.google_mimetype ? getGoogleDriveUrl(resource) : resource.url;

  if (!resourceUrl && resource.type === ResourceTypeType.GoogleDrive && resource.google_mimetype) {
    return <ResourceLoadingError />;
  }

  if (!resourceUrl) return null;

  if (resource.google_mimetype) {
    if (resourceUrl.includes("?")) {
      resourceUrl = `${resourceUrl}&`;
    } else {
      resourceUrl = `${resourceUrl}?`;
    }
    resourceUrl += "embedded=true&rm=embedded"; // prevents Google doc from stealing focus
  }

  const handleReload = () => {
    if (iframeRef.current && resourceUrl) {
      iframeRef.current.src = resourceUrl;
    }
  };

  return (
    <div
      className="flex flex-column w-full"
      style={{
        height: "99%", // prevent unnecessary scrollbar which for some reason appears when height is 100%
      }}
    >
      <div className="flex items-center p1 bg-black body-s text-black-tint-70 resource-controls">
        <ResourceTitle resource={resource} />
        <TooltipErrorBoundary text="The refresh button cannot be displayed">
          <IconButton aria-label="Reload resource" onClick={handleReload}>
            <RefreshIcon width={16} height={16} color="var(--black-tint-70)" />
          </IconButton>
        </TooltipErrorBoundary>
        <TooltipErrorBoundary text="The open-in-a-new-tab button cannot be displayed">
          <OpenInNewTab url={resourceUrl} />
        </TooltipErrorBoundary>
      </div>

      <ExternalResourceWarning />

      <CenteredErrorBoundary text="The resource cannot be displayed">
        <div className="bg-white flex-auto">
          <iframe
            title={resource.title}
            src={resourceUrl}
            ref={iframeRef}
            className="w-full h-full"
            style={
              localModalContent !== LocalModalContentType.None ? { pointerEvents: "none" } : {}
            }
          />
        </div>
      </CenteredErrorBoundary>
    </div>
  );
}

export { IframeResource };
